import React from 'react';
import moment from 'moment';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import { TextField } from 'formik-material-ui';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import DialogPaper from '../DialogPaper';

import * as ProfileService from '../../api/profileService';

export interface Values {
  day: string;
  month: string;
  year: string;
}

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.palette.common.white,
    color: '#000000',
    marginBottom: theme.spacing(6),
  },
  buttonContainer: {
    width: 100,
    margin: theme.spacing(),
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  textField: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  grow: {
    flexGrow: 1,
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  toolbar: {
    display: 'flex',
    paddingLeft: 12,
    paddingRight: 12,
  },
  box: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  close: {
    marginRight: 'auto',
  },
  actions: {
    marginLeft: 'auto',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    [theme.breakpoints.down('xs')]: {
      width: 150,
    },
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
}));

type Props = Pick<DialogProps, 'open' | 'onClose'> & {
  profile: ProfileService.Profile;
  confirmUpdate: (status: boolean) => void;
};

export default function EditDOBDialog({
  profile,
  onClose,
  open,
  confirmUpdate,
}: Props) {
  const classes = useStyles();
  const currentYear = new Date().getFullYear();
  const ValidationSchema = Yup.object().shape({
    day: Yup.string()
      .required('Required')
      .matches(/^\d{1,2}$/, 'Invalid day')
      .test('is-valid-day', 'Day must be between 1 and 31', (value) => {
        if (!value) return false;
        const day = parseInt(value, 10);
        return day >= 1 && day <= 31;
      }),
    month: Yup.string()
      .required('Required')
      .matches(/^\d{1,2}$/, 'Invalid month')
      .test('is-valid-month', 'Month must be between 1 and 12', (value) => {
        if (!value) return false;
        const month = parseInt(value, 10);
        return month >= 1 && month <= 12;
      }),
    year: Yup.string()
      .required('Required')
      .matches(/^\d{4}$/, 'Year must be 4 digits')
      .test('is-valid-year', 'Year must be less than current year', (value) => {
        if (!value) return false;
        const year = parseInt(value, 10);
        return year > 1900 && year < currentYear;
      }),
  });

  const initialValues: Values = {
    day:
      !profile.dob || profile.dob === -62135596800
        ? ''
        : moment.unix(profile.dob).format('DD'),
    month:
      !profile.dob || profile.dob === -62135596800
        ? ''
        : moment.unix(profile.dob).format('MM'),
    year:
      !profile.dob || profile.dob === -62135596800
        ? ''
        : moment.unix(profile.dob).format('YYYY'),
  };

  const handleSubmit = async (
    values: Values,
    { setFieldError, setSubmitting }: FormikHelpers<Values>,
  ) => {
    if (values.day && values.month && values.year) {
      console.log('values', values);
      try {
        await ProfileService.update(
          profile.gender,
          `${values.year}-${values.month}-${values.day}`,
          profile.firstName,
          profile.lastName,
          profile.email,
          profile.mobNum,
          profile.alt_email,
          profile.company,
        );
        confirmUpdate(true);
        setSubmitting(false);
      } catch (e: any) {
        console.log(e);
        e.data.forEach((err: any) => {
          setFieldError(err.meta.attribute, err.detail);
        });
        setSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose({}, 'backdropClick');
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperComponent={DialogPaper}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        {({ isSubmitting, dirty }) => {
          return (
            <>
              <Form noValidate>
                <AppBar className={classes.appBar} position="sticky">
                  <Toolbar className={classes.toolbar}>
                    <div className={classes.box}>
                      <div className={classes.close}>
                        <IconButton onClick={handleClose}>
                          <CloseIcon className={classes.closeIcon} />
                        </IconButton>
                      </div>
                    </div>
                    <div className={classes.box}>
                      <Typography variant="h6" className={classes.title}>
                        Edit Birthday
                      </Typography>
                    </div>
                    <div className={classes.box}>
                      <div className={classes.actions}></div>
                    </div>
                  </Toolbar>
                </AppBar>
                <Container maxWidth="md">
                  <div className={classes.cardContainer}>
                    <div style={{ marginBottom: 8 }}>
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 500, marginBottom: 4 }}
                      >
                        Birthday (YYYY-MM-DD)
                      </Typography>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginTop: 16,
                        marginBottom: 8,
                      }}
                    >
                      <Field
                        component={TextField}
                        name="year"
                        label=""
                        placeholder="YYYY"
                        inputProps={{
                          maxLength: 4,
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                        }}
                        type="tel"
                        style={{ width: 80 }}
                        helperText=""
                        onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            '',
                          );
                        }}
                      />
                      <span>-</span>
                      <Field
                        component={TextField}
                        name="month"
                        label=""
                        placeholder="MM"
                        inputProps={{
                          maxLength: 2,
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                        }}
                        type="tel"
                        style={{ width: 60 }}
                        helperText=""
                        onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            '',
                          );
                        }}
                      />
                      <span>-</span>
                      <Field
                        component={TextField}
                        name="day"
                        label=""
                        placeholder="DD"
                        inputProps={{
                          maxLength: 2,
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                        }}
                        type="tel"
                        style={{ width: 60 }}
                        helperText=""
                        onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            '',
                          );
                        }}
                      />
                    </div>
                    <div className={classes.actionContainer}>
                      <Button
                        onClick={() => {
                          if (onClose) {
                            onClose({}, 'backdropClick');
                          }
                        }}
                        className={classes.buttonContainer}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        color="primary"
                        className={classes.buttonContainer}
                        disabled={isSubmitting || !dirty}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </Container>
              </Form>
            </>
          );
        }}
      </Formik>
    </Dialog>
  );
}
